/**
 * Macros — bridge-executed multi-lane MIDI sequences.
 *
 * Mental model:
 *   - Macro = one button. Tap to arm at next bar 1, tap again to kill.
 *   - Each macro contains 1..N lanes. Each lane drives one MIDI target
 *     with a shape (curve) over a length in beats.
 *   - Lane lengths can exceed the macro's triggerLength (the "tails carry
 *     over" case — fills that finish bar 19 even though the macro nominally
 *     ended at bar 16).
 *
 * Storage:
 *   Macros live in their own document (a "macro library") referenced by
 *   layouts. A layout's button references a macro by id; the macro itself
 *   is reusable across layouts and projects.
 *
 * Runtime contract:
 *   - PWA edits the document and sends `macro.save` to the bridge.
 *   - PWA fires by sending `macro.arm <id>`. Bridge quantizes to next bar 1,
 *     runs each lane, emits MIDI per its shape, and broadcasts `macro.state`
 *     so the PWA can render arm/run/kill UI.
 *   - PWA kills by sending `macro.arm <id>` again (same id = toggle). Bridge
 *     snaps each lane to its center value and emits one final CC/note-off.
 *
 * Versioning:
 *   `schemaVersion: 'opbuttons-macros/v1'` at the document root. v1 supports
 *   only CC and noteon/noteoff targets; pitchbend/program/OSC reserved for
 *   later.
 */

import type { LaneDmxTarget } from './fixture.js';

// =====================================================================
// MIDI target
// =====================================================================

/** Which bridge instance runs this lane. v0 only has 'pc'; 'mac' is reserved
 *  for the v0.2 dual-bridge milestone. */
export type MacroBridgeTarget = 'pc' | 'mac' | 'all';

/** Subset of MIDI message kinds macros support in v1. Pitchbend and program
 *  change are reserved for a later version. */
export type MacroMidiKind = 'cc' | 'noteon' | 'noteoff';

export interface MacroMidiTarget {
  bridge: MacroBridgeTarget;
  kind: MacroMidiKind;
  /** 0–15. */
  channel: number;
  /** For cc: controller number. For noteon/noteoff: note number. 0–127. */
  number: number;
}

// =====================================================================
// Shape library
// =====================================================================

/**
 * Shape kinds map to a value(t)→0..1 curve.  `t` is 0..1 along the lane's
 * length.  The lane wraps the result into the target's value range
 * via [center, amplitude] (same convention as LFO engine).
 *
 *   sine    — half-sine arch.                  /‾\
 *   exp     — exponential ramp up.             _/
 *   scurve  — smooth S from 0 to 1.            _/‾
 *   stepped — N flat steps from 0 to 1.        __/‾
 *   ramp    — linear from 0 to 1.              /
 *   dip     — ramp down then back.             \/
 *   hold    — flat at value=1 the whole time.  ‾‾‾
 */
export type MacroShapeKind = 'sine' | 'exp' | 'scurve' | 'stepped' | 'ramp' | 'dip' | 'hold';

/**
 * Warp parameters applied AFTER the base shape's value(t) is computed.
 *
 *   skew      — bias the time axis. <0 weights early, >0 weights late.   -1..1
 *   amplitude — scale the output. 1.0 = full range, 0.5 = half.          0..1
 *   offset    — add to the output AFTER amplitude scaling.                -1..1
 *   reverse   — mirror the curve along the time axis.
 *
 * For 'stepped' an additional `steps` field (2..16) controls step count.
 */
export interface MacroShape {
  kind: MacroShapeKind;
  /** Shape-specific extras. Optional fields, defaults defined per kind. */
  steps?: number;   // only meaningful for 'stepped' (default 4)
  // — warps —
  skew?: number;
  amplitude?: number;
  offset?: number;
  reverse?: boolean;
}

// =====================================================================
// Lane
// =====================================================================

export interface MacroLane {
  /** Stable id within the macro. ULID. */
  id: string;
  /** Human label for the editor; not used at runtime. */
  label?: string;
  /** Where this lane sends its values: MIDI, or a light (`LaneDmxTarget`,
   *  told apart by `isDmxLaneTarget`). */
  target: MacroMidiTarget | LaneDmxTarget;
  /** Lane length in beats. May exceed the macro's triggerLength — that's
   *  how tails are expressed (e.g. trigger=64 beats / 16 bars, lane.length=80
   *  means this lane runs into bar 20 even though the macro nominally
   *  ended at bar 16). */
  lengthBeats: number;
  /** Shape evaluated over t = 0..1 along lengthBeats. */
  shape: MacroShape;
  /** Center value, 0..127. The lane's neutral resting value. On kill the
   *  bridge emits one final message at this value so the modulated
   *  parameter doesn't freeze wherever the shape happened to be. */
  center: number;
  /** Amplitude in CC units, 0..127. Half-range from center.  Value at
   *  shape(t)=1 is center + amplitude (clamped 0..127); value at shape(t)=0
   *  is center - amplitude. */
  amplitude: number;
}

// =====================================================================
// Macro
// =====================================================================

export interface Macro {
  /** Stable id. Referenced from layouts as the "macroId" of a button. */
  id: string;
  /** ms epoch of the last local edit. Decides which copy wins when project
   *  sync brings the same macro from another device (pwa
   *  `layout/libraryMerge.ts`). Optional: added 2026-09-28, absent on older
   *  entries, which compare as 0. Additive, no schema bump. */
  modifiedAt?: number;
  /** Human label shown on the button face. */
  name: string;
  /** Macro-level length in beats. Used for two things:
   *   1. The "armed → next bar 1" anchor (no effect on length).
   *   2. Arming gate: tapping the button a second time within
   *      [armTime, armTime + triggerLength] kills the macro.
   *  Lane lengths can exceed this to express tails. */
  triggerLengthBeats: number;
  lanes: MacroLane[];
  /** Optional MIDI trigger binding — an incoming MIDI message that arms
   *  this macro just like a button tap. Reserved for Milestone 4; safe to
   *  store but bridge ignores in M1. */
  midiTrigger?: {
    kind: 'noteon' | 'cc';
    channel: number;
    /** Note number or CC number to match. */
    number: number;
  };
}

// =====================================================================
// Macro library document
// =====================================================================

export const MACROS_SCHEMA_VERSION = 'opbuttons-macros/v1' as const;

export interface MacroLibrary {
  schemaVersion: typeof MACROS_SCHEMA_VERSION;
  macros: Macro[];
}
